import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "alaine_access_token";

export function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (err) { console.warn("Token storage failed", err); }
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Bearer token from localStorage as a fallback when cookies don't persist
// (HttpOnly cookie still primary; backend accepts either).
api.interceptors.request.use((config) => {
  const t = getStoredToken();
  if (t && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Surface specific 403 codes as branded toasts with a single-tap upgrade CTA.
// This way every call site doesn't need its own error handler for tier
// limits or demo-mode write attempts — the interceptor does it once.
function maybeShowTierToast(error) {
  const detail = error?.response?.data?.detail;
  if (!detail || typeof detail !== "object") return false;
  // Aug 2026: public subscriptions retired — no more Upgrade/Sign up CTAs.
  // Limits and demo restrictions surface as plain messages; access questions
  // go to Alaine & Co. directly.
  if (detail.error === "tier_limit_exceeded") {
    toast.error(detail.message || "You've hit your plan limit. Contact Alaine & Co. about your account.", {
      duration: 8000,
    });
    return true;
  }
  if (detail.error === "demo_read_only") {
    toast.info(detail.message || "Demo mode is read-only.", { duration: 7000 });
    return true;
  }
  return false;
}

// On 401, clear the stale token so the user is forced to re-login cleanly.
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401) setStoredToken(null);
    if (error?.response?.status === 403) {
      // Mark so call sites can skip their own toast if we already showed one.
      if (maybeShowTierToast(error)) error.handledToast = true;
    }
    // Network failure (offline / DNS / CORS / dropped request) or any 5xx —
    // surface a single branded toast instead of leaving the user staring at
    // a half-loaded page. Call sites can suppress with config.skipNetToast.
    const skip = error?.config?.skipNetToast || error.handledToast;
    if (!skip) {
      if (!error?.response) {
        toast.error("Network hiccup — check your connection and try again.", {
          duration: 5000,
        });
        error.handledToast = true;
      } else if (error.response.status >= 500) {
        toast.error("Our server hit a snag. We're on it — try again in a moment.", {
          duration: 5000,
        });
        error.handledToast = true;
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
